import {
  IonContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonPage,
} from '@ionic/react';
import { closeOutline, expandOutline } from 'ionicons/icons';
import { ReactElement, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '../../api/authorization.provider';
import {
  Chart,
  PlanEntitlementKey,
  useGetTeamQuery,
} from '../../api/graphql/generated';
import { BookmarkType } from '../../api/hooks/bookmark.hooks';
import { useFullScreenMode } from '../../api/hooks/full-screen-mode.hooks';
import { PageHeader } from '../../components/header/page.header';
import { addChart } from '../../components/icons/add-chart';
import { PaywallIonButton } from '../../components/paywall/paywall-ion-button';
import { TeamHome } from '../../components/team/team-home';
import { trackInitiateCreateChart } from '../../config/analytics';
import { useBeaconRouter } from '../../route/route.hooks';

interface TeamHomePageProps {
  id: string;
}

const TeamHomePage = (props: TeamHomePageProps): ReactElement => {
  const { id } = props;
  const { i18n } = useTranslation();
  const { data } = useGetTeamQuery({ variables: { input: { id } } });

  let title: string = i18n.t('common:team');
  let description;

  if (data?.getTeam) {
    title = `${data.getTeam.name} - ${i18n.t('common:home')}`;
    description = data.getTeam.description ?? undefined;
  }

  const { builder } = useBeaconRouter();

  const chartPermissionObj: Partial<Chart> = {
    parentId: id,
  };

  const hasChartCreatePermission = useHasPermission(
    'create',
    'chart',
    chartPermissionObj
  );

  const hasChartFullscreenPermission = useHasPermission(
    'fullscreen',
    'chart',
    chartPermissionObj
  );

  const {
    requestFullScreen,
    isFullScreen,
    closeFullScreen,
    isFullScreenSupported,
  } = useFullScreenMode();

  const pageRef = useRef<HTMLDivElement | null>(null);

  const createChartEnabled = hasChartCreatePermission && !isFullScreen;

  return (
    <IonPage ref={pageRef}>
      <PageHeader
        title={title}
        subtitle={description}
        rightButtons={
          isFullScreenSupported ? (
            <PaywallIonButton
              planEntitlementKey={PlanEntitlementKey.CHART_FULLSCREEN}
              isFeatureAvailable={() => hasChartFullscreenPermission}
              ionButtonProps={{
                onClick: () => {
                  if (isFullScreen) {
                    closeFullScreen();
                  } else {
                    if (pageRef.current) {
                      requestFullScreen(pageRef.current);
                    }
                  }
                },
              }}
            >
              <IonIcon icon={isFullScreen ? closeOutline : expandOutline} />
            </PaywallIonButton>
          ) : undefined
        }
        bookmarkProps={{
          enable: true,
          type: BookmarkType.TEAM,
        }}
      />
      <IonContent>
        <TeamHome id={id} />
        {createChartEnabled && (
          <IonFab vertical="bottom" horizontal="end" slot="fixed">
            <IonFabButton
              routerLink={builder.getTeamChartTemplatesRoute(id)}
              onClick={() => {
                trackInitiateCreateChart();
              }}
            >
              <IonIcon icon={addChart} />
            </IonFabButton>
          </IonFab>
        )}
      </IonContent>
    </IonPage>
  );
};

export { TeamHomePage };
